import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import { useRag } from "../RagContext";
import ConversationSidebar from "./ConversationSidebar";
import MessageBubble from "./MessageBubble";
import "./ChatSection.css";

export default function ChatSection() {
    const {
        currentBandoId,
        currentConversationId,
        conversations,
        messages,
        streaming,
        loading,
        loadConversations,
        sendMessage,
        createConversation,
    } = useRag();

    const [input, setInput] = useState("");
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const textareaRef = useRef(null);
    // Auto-scroll engaged finche' l'utente non scrolla manualmente verso l'alto.
    const shouldAutoScrollRef = useRef(true);
    // Throttle dello scroll durante streaming: scrollare ad ogni token
    // crea jitter continuo che impedisce di leggere il testo. Limito a max
    // 1 scroll ogni 600ms durante streaming.
    const lastScrollTimeRef = useRef(0);

    const currentConversation = conversations.find((c) => c.id === currentConversationId) || null;

    // Quando cambia il bando, carica le conversazioni di quel bando.
    useEffect(() => {
        if (currentBandoId) {
            loadConversations(currentBandoId);
        }
    }, [currentBandoId, loadConversations]);

    // Disengagement detection
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const handleScroll = () => {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            shouldAutoScrollRef.current = distanceFromBottom < 100;
        };
        el.addEventListener("scroll", handleScroll, { passive: true });
        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    // Auto-scroll con throttle durante streaming (max 1 scroll ogni 600ms)
    useEffect(() => {
        if (!shouldAutoScrollRef.current) return;
        const now = Date.now();
        if (streaming.isStreaming && now - lastScrollTimeRef.current < 600) return;
        lastScrollTimeRef.current = now;
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: streaming.isStreaming ? "auto" : "smooth",
            });
        }
    }, [messages, streaming.partialContent, streaming.isStreaming]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || streaming.isStreaming) return;

        let conversationId = currentConversationId;
        if (!conversationId) {
            const conv = await createConversation(currentBandoId || null);
            conversationId = conv.id;
        }

        const msg = input;
        setInput("");
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
        }

        shouldAutoScrollRef.current = true;
        await sendMessage(conversationId, msg);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const handleTextareaChange = (e) => {
        setInput(e.target.value);
        const ta = e.target;
        ta.style.height = "auto";
        ta.style.height = Math.min(ta.scrollHeight, 150) + "px";
    };

    // Durante streaming nascondiamo le sources: arrivano dal backend prima
    // dei token (evento 'sources' precede gli 'token' SSE) e altrimenti
    // comparirebbero in cima alla bolla assistant ancora vuota. Le sources
    // vengono mostrate quando lo streaming finisce e il messaggio finale
    // viene appeso a state.messages con il suo array sources completo.
    const streamingBubble = streaming.isStreaming
        ? {
              id: "streaming",
              conversationId: currentConversationId,
              role: "assistant",
              content: streaming.partialContent,
              sources: null,
              createdAt: new Date().toISOString(),
          }
        : null;

    if (!currentBandoId) {
        return (
            <div className="rag-chat-section__empty">
                <p>Seleziona un bando per iniziare a chattare.</p>
            </div>
        );
    }

    return (
        <div className="rag-chat-section">
            <ConversationSidebar />

            <div className="rag-chat-section__main">
                {!currentConversation ? (
                    <div className="rag-chat-section__placeholder">
                        <h3>Inizia una nuova conversazione</h3>
                        <p>Seleziona una conversazione dalla sidebar o creane una nuova.</p>
                    </div>
                ) : (
                    <>
                        <div className="rag-chat-section__header">
                            <h3>{currentConversation.title || "Nuova conversazione"}</h3>
                        </div>

                        <div ref={scrollContainerRef} className="rag-chat-section__messages">
                            {loading.messages && messages.length === 0 ? (
                                <p className="rag-chat-section__loading">Caricamento messaggi…</p>
                            ) : (
                                messages.map((msg) => <MessageBubble key={msg.id} message={msg} />)
                            )}

                            {streamingBubble && <MessageBubble message={streamingBubble} isStreaming />}

                            <div ref={messagesEndRef} />
                        </div>

                        <form onSubmit={handleSubmit} className="rag-chat-section__composer">
                            <textarea
                                ref={textareaRef}
                                value={input}
                                onChange={handleTextareaChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Scrivi un messaggio…"
                                rows={1}
                                disabled={streaming.isStreaming}
                                className="rag-chat-section__textarea"
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || streaming.isStreaming}
                                className="rag-chat-section__send"
                                aria-label="Invia"
                            >
                                <Send size={18} />
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
